import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  InputAdornment,
  CircularProgress,
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterIcon from '@mui/icons-material/Filter';
import LockIcon from '@mui/icons-material/Lock';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import { countries, getCitiesByCountry, City } from '../data/locations';
import BusinessAdCard from '../components/BusinessAdCard';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';
import { C } from '../theme';

interface BusinessAd {
  id: number;
  title: string;
  description: string | null; // Can be null for limited access
  price: number;
  business_type: string;
  country_id: string;
  city_id: string;
  annual_revenue?: number | null;
  employee_count?: number | null;
  established_year?: number | null;
  author_name: string;
  thumbnail?: string;
  created_at: string;
}

const businessTypes = [
  'Restoran i ugostiteljstvo',
  'Trgovina i usluge',
  'Proizvodnja',
  'Tehnologija i IT',
  'Zdravstvo',
  'Obrazovanje',
  'Transport i logistika',
  'Nekretnine',
  'Finansije i bankarstvo',
  'Ostalo',
];

const businessTypeMap: Record<string, string> = {
  'Restoran i ugostiteljstvo': 'restaurant_hospitality',
  'Trgovina i usluge': 'retail_services',
  'Proizvodnja': 'manufacturing',
  'Tehnologija i IT': 'technology_it',
  'Zdravstvo': 'healthcare',
  'Obrazovanje': 'education',
  'Transport i logistika': 'transport_logistics',
  'Nekretnine': 'real_estate',
  'Finansije i bankarstvo': 'finance_banking',
  'Ostalo': 'other',
};

const glassCardSx = {
  p: { xs: 4, md: 5 },
  backgroundColor: 'rgba(18,18,20,0.92)',
  backdropFilter: 'blur(20px)',
  borderRadius: 4,
  mx: 'auto',
  border: `1px solid ${C.line}`,
  boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
};

const BusinessAds: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [businessAds, setBusinessAds] = useState<BusinessAd[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedBusinessType, setSelectedBusinessType] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minRevenue, setMinRevenue] = useState('');
  const [maxRevenue, setMaxRevenue] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [availableCities, setAvailableCities] = useState<City[]>([]);

  useEffect(() => {
    fetchBusinessAds();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      setAvailableCities(getCitiesByCountry(Number(selectedCountry)));
      setSelectedCity('');
    } else {
      setAvailableCities([]);
    }
  }, [selectedCountry]);

  const fetchBusinessAds = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/business-ads');
      setBusinessAds(response.data);
    } catch (err) {
      setError('Greška pri učitavanju oglasa za biznise');
      console.error('Error fetching business ads:', err);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredBusinessAds = () => {
    return businessAds.filter(ad => {
      if (searchTerm && !ad.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !(ad.description && ad.description.toLowerCase().includes(searchTerm.toLowerCase()))) {
        return false;
      }
      if (selectedCountry && ad.country_id !== selectedCountry) return false;
      if (selectedCity && ad.city_id !== selectedCity) return false;
      if (selectedBusinessType && ad.business_type !== businessTypeMap[selectedBusinessType]) return false;
      if (minPrice && ad.price < Number(minPrice)) return false;
      if (maxPrice && ad.price > Number(maxPrice)) return false;
      if (minRevenue && (!ad.annual_revenue || ad.annual_revenue < Number(minRevenue))) return false;
      if (maxRevenue && (!ad.annual_revenue || ad.annual_revenue > Number(maxRevenue))) return false;
      return true;
    });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCountry('');
    setSelectedCity('');
    setSelectedBusinessType('');
    setMinPrice('');
    setMaxPrice('');
    setMinRevenue('');
    setMaxRevenue('');
  };

  const filteredAds = getFilteredBusinessAds();

  if (loading) {
    return (
      <Box sx={{ pt: '80px', pb: 6, backgroundColor: C.bg, minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: C.gold }} />
      </Box>
    );
  }

  return (
    <>
      <SEO
        title="Biznisi"
        description="Pretražite oglase za biznis prilike. Pronađite odlične biznis prilike i povežite se sa vlasnicima biznisa."
        keywords="biznis, poslovne prilike, investicija, IT, restoran, trgovina, proizvodnja, tehnologija, zdravstvo, obrazovanje, platforma za investicije"
        url="/business-ads"
      />
      <Box sx={{ pt: '70px', backgroundColor: C.bg, minHeight: '100vh' }}>
        {/* Hero Section */}
        <Box
          sx={{
            background: `linear-gradient(135deg, rgba(10,10,11,0.86) 0%, rgba(10,10,11,0.72) 100%), url('/images/office.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            color: C.white,
            py: { xs: 10, md: 16 },
            textAlign: 'center',
            position: 'relative',
            minHeight: '80vh',
            display: 'flex',
            alignItems: 'center',
            borderBottom: `1px solid ${C.line}`,
          }}
        >
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
            <Typography
              variant="h1"
              sx={{ fontWeight: 800, mb: 3, fontSize: { xs: '2.5rem', md: '4rem' }, lineHeight: 1.1, letterSpacing: '-1px' }}
            >
              Biznisi
            </Typography>
            <Typography
              variant="h5"
              sx={{ mb: 6, color: 'rgba(255,255,255,0.85)', maxWidth: 700, mx: 'auto', lineHeight: 1.6, fontWeight: 400, fontSize: { xs: '1.1rem', md: '1.3rem' } }}
            >
              Pronađite odlične biznis prilike i povežite se sa vlasnicima biznisa
            </Typography>

            {/* Search Section - Only show if authenticated */}
            {isAuthenticated && (
              <Paper elevation={0} sx={{ ...glassCardSx, maxWidth: 900 }}>
                <Typography variant="h4" sx={{ mb: 3, color: C.white, fontWeight: 700, textAlign: 'center' }}>
                  Pretraži oglase biznisa
                </Typography>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      placeholder="Pretraži oglase..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: C.muted }} /></InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Država</InputLabel>
                      <Select value={selectedCountry} label="Država" onChange={(e) => setSelectedCountry(e.target.value)}>
                        <MenuItem value="">Sve države</MenuItem>
                        {countries.map((country) => (
                          <MenuItem key={country.id} value={country.id}>
                            {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button variant="contained" fullWidth size="large" onClick={() => setShowFilters(!showFilters)} startIcon={<FilterIcon />} sx={{ py: 1.5 }}>
                      Filteri
                    </Button>
                  </Grid>
                </Grid>

                {/* Advanced Filters */}
                {showFilters && (
                  <Box sx={{ mt: 4, pt: 4, borderTop: `1px solid ${C.line}` }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={3}>
                        <TextField fullWidth label="Minimalna cena" type="number" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }} />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField fullWidth label="Maksimalna cena" type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }} />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Tip biznisa</InputLabel>
                          <Select value={selectedBusinessType} label="Tip biznisa" onChange={(e) => setSelectedBusinessType(e.target.value)}>
                            <MenuItem value="">Svi tipovi</MenuItem>
                            {businessTypes.map((type) => (
                              <MenuItem key={type} value={type}>
                                {type}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Button variant="outlined" fullWidth onClick={clearFilters} sx={{ py: 1.5 }}>
                          Resetuj
                        </Button>
                      </Grid>
                    </Grid>
                    <Grid container spacing={3} sx={{ mt: 2 }}>
                      <Grid item xs={12} md={3}>
                        <TextField fullWidth label="Minimalna godišnja zarada" type="number" value={minRevenue} onChange={(e) => setMinRevenue(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }} />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField fullWidth label="Maksimalna godišnja zarada" type="number" value={maxRevenue} onChange={(e) => setMaxRevenue(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }} />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Grad</InputLabel>
                          <Select value={selectedCity} label="Grad" onChange={(e) => setSelectedCity(e.target.value)} disabled={!selectedCountry}>
                            <MenuItem value="">Svi gradovi</MenuItem>
                            {availableCities.map((city) => (
                              <MenuItem key={city.id} value={city.id}>
                                {city.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Paper>
            )}

            {/* Login Prompt for non-authenticated users */}
            {!isAuthenticated && (
              <Paper elevation={0} sx={{ ...glassCardSx, maxWidth: 600, textAlign: 'center' }}>
                <LockIcon sx={{ fontSize: 60, color: C.gold, mb: 3 }} />
                <Typography variant="h4" sx={{ mb: 2, color: C.white, fontWeight: 700 }}>
                  Prijavite se za pristup oglasima
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, color: C.muted, lineHeight: 1.6 }}>
                  Da biste videli dostupne biznis oglase i povezali se sa vlasnicima biznisa ili investitorima,
                  potrebno je da se prijavite na svoj nalog.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button variant="contained" size="large" onClick={() => navigate('/login')} sx={{ px: 4, py: 1.5 }}>
                    Prijavi se
                  </Button>
                  <Button variant="outlined" size="large" onClick={() => navigate('/register')} sx={{ px: 4, py: 1.5 }}>
                    Registruj se
                  </Button>
                </Box>
              </Paper>
            )}
          </Container>
        </Box>

        {/* Results Section - Only show if authenticated */}
        {isAuthenticated && (
          <Container maxWidth="lg" sx={{ py: 8 }}>
            <Box sx={{ mb: 6, textAlign: 'center' }}>
              <Typography variant="h2" sx={{ fontWeight: 800, mb: 2, color: C.white }}>
                Dostupni oglasi za biznise
              </Typography>
              <Typography variant="h6" sx={{ color: C.muted, maxWidth: 600, mx: 'auto', fontWeight: 400 }}>
                Pronađite savršenu biznis priliku ili povežite se sa investitorima
              </Typography>
              {user?.userType === 'business_owner' && (
                <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => navigate('/create-business-ad')} sx={{ mt: 3 }}>
                  Postavi oglas za biznis
                </Button>
              )}
            </Box>

            {error && (
              <Typography color="error" textAlign="center" sx={{ mb: 4 }}>
                {error}
              </Typography>
            )}

            {filteredAds.length === 0 && !loading ? (
              <Box textAlign="center" sx={{ py: 8 }}>
                <Typography variant="h6" sx={{ mb: 2, color: C.white }}>
                  Nema pronađenih oglasa
                </Typography>
                <Typography sx={{ color: C.muted }}>
                  Pokušajte da promenite kriterijume pretrage
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={4}>
                {filteredAds.map((businessAd) => (
                  <Grid item xs={12} sm={6} md={4} key={businessAd.id}>
                    <BusinessAdCard businessAd={businessAd} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Container>
        )}
      </Box>
    </>
  );
};

export default BusinessAds;
